import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toBlob } from 'html-to-image';
import { toast } from 'sonner';
import { Download, Save, Plus, Trash2, Upload, FilePlus2 } from 'lucide-react';
import { DoctorCardCanvas, CARD_ICONS, CARD_ICON_LABELS, CARD_FORMATS, CARD_LANGUAGES, CARD_FONTS } from './DoctorCardCanvas';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SAMPLE = {
  photo: '/card/sample-doctor.jpg', photoX: 50, photoY: 25, photoZoom: 100, lineT: 3, decor: 'line', waveT: 100,
  name: 'Δρ. Σίμος Καζαμίας', title: 'Προσωπικός Ιατρός',
  bio: 'Ο Δρ. Σίμος Καζαμίας είναι Προσωπικός Ιατρός με εμπειρία στην πρόληψη, διάγνωση και αντιμετώπιση προβλημάτων υγείας για κάθε ηλικία.\n\nΠαρέχει ολοκληρωμένη πρωτοβάθμια φροντίδα, με έμφαση στην ανθρώπινη προσέγγιση και τη συνεχή υποστήριξη των ασθενών του.',
  services: [
    { icon: 'drip', text: 'Ενδοφλέβια έγχυση υγρών και πολυβιταμινών (IV drip therapy) για ταχεία απορρόφηση και άμεση ενυδάτωση.' },
    { icon: 'ship', text: 'Εγκεκριμένος Ιατρός Ναυτιλιακών Ιατρικών Εξετάσεων.' },
    { icon: 'anchor', text: 'Έκδοση Ιατρικών Πιστοποιητικών για Χειριστές Ταχύπλων Σκαφών.' },
    { icon: 'lab', text: 'Διενέργεια Ελέγχου Ναρκωτικών & Αλκοόλης (Drug & Alcohol Testing) και Έκδοση Πιστοποιητικών.' },
    { icon: 'stethoscope', text: 'Πρόληψη και διαχείριση προβλημάτων υγείας.' },
    { icon: 'clipboard', text: 'Παρακολούθηση χρόνιων νόσων και τακτικός έλεγχος.' },
    { icon: 'users', text: 'Συμβουλές για υγιεινό τρόπο ζωής και ευεξία.' },
    { icon: 'refresh', text: 'Παραπομπές σε ιατρούς άλλων ειδικοτήτων όταν απαιτείται.' },
  ],
  phones: 'Ιατρείο: 22358989 & 22323314\nΚινητό: 99228989',
  location: 'Λευκωσία, Λατσιά',
  follow: 'https://linktr.ee/drkazamias',
  followLabel: 'Follow us on',
  phonesLabel: 'Τηλέφωνα επικοινωνίας',
  locationLabel: 'Τοποθεσία ιατρείου',
  langLabel: 'Γλώσσες επικοινωνίας',
  languages: ['gr', 'gb'],
};

const EMPTY = { ...SAMPLE, photo: '', name: '', title: '', bio: '', services: [{ icon: 'stethoscope', text: '' }], phones: '', location: '', follow: '' };

const Field = ({ label, children }) => (
  <label className="block">
    <span className="block text-xs uppercase tracking-wider text-gold/80 mb-1">{label}</span>
    {children}
  </label>
);
const inputCls = 'w-full bg-navy-200/60 border border-gold/20 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-gold/60';

const ScaledPreview = ({ d, format, canvasRef }) => {
  const { w, h } = CARD_FORMATS[format];
  const scale = 380 / w;
  return (
    <div style={{ width: w * scale, height: h * scale }} className="relative rounded-lg overflow-hidden ring-1 ring-gold/30 shadow-2xl">
      <div style={{ transform: `scale(${scale})`, transformOrigin: 'top left' }}>
        <DoctorCardCanvas ref={canvasRef} d={d} format={format} />
      </div>
    </div>
  );
};

const DEF_BOLD = { name: true, title: true, langs: true };

const StyleControl = ({ d, up, k, label }) => {
  const o = (d.styles || {})[k] || {};
  const set = (patch) => up({ styles: { ...(d.styles || {}), [k]: { ...o, ...patch } } });
  const bold = o.bold ?? !!DEF_BOLD[k];
  const btn = (on) => `w-8 h-8 rounded-md border text-sm ${on ? 'border-gold bg-gold/20 text-gold' : 'border-gold/20 text-slate-300'}`;
  return (
    <div className="rounded-lg border border-gold/15 bg-navy-200/30 p-2.5 space-y-2" data-testid={`card-style-${k}`}>
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-[11px] uppercase tracking-wider text-gold/70 mr-1">Στυλ: {label}</span>
        <select value={o.font || ''} onChange={(e) => set({ font: e.target.value || undefined })} className="bg-navy-200/60 border border-gold/20 rounded-md px-2 py-1 text-xs text-white" data-testid={`card-style-${k}-font`}>
          <option value="">Προεπιλογή</option>
          {Object.entries(CARD_FONTS).map(([f, v]) => <option key={f} value={f}>{v.label}</option>)}
        </select>
        <button type="button" onClick={() => set({ bold: !bold })} className={`${btn(bold)} font-bold`} data-testid={`card-style-${k}-bold`}>B</button>
        <button type="button" onClick={() => set({ italic: !o.italic })} className={`${btn(o.italic)} italic`} data-testid={`card-style-${k}-italic`}>I</button>
        <button type="button" onClick={() => up({ styles: { ...(d.styles || {}), [k]: {} } })} className="text-[11px] text-slate-400 hover:text-gold ml-auto" data-testid={`card-style-${k}-reset`}>Επαναφορά</button>
      </div>
      <div className="grid grid-cols-3 gap-2 text-[11px] text-slate-400">
        <label>Μέγεθος {o.size ?? 100}%<input type="range" min="50" max="160" value={o.size ?? 100} onChange={(e) => set({ size: +e.target.value })} className="w-full accent-[#d4af37]" data-testid={`card-style-${k}-size`} /></label>
        <label>Πάνω / Κάτω {o.y || 0}<input type="range" min="-120" max="120" value={o.y || 0} onChange={(e) => set({ y: +e.target.value })} className="w-full accent-[#d4af37]" data-testid={`card-style-${k}-y`} /></label>
        <label>Αριστερά / Δεξιά {o.x || 0}<input type="range" min="-120" max="120" value={o.x || 0} onChange={(e) => set({ x: +e.target.value })} className="w-full accent-[#d4af37]" data-testid={`card-style-${k}-x`} /></label>
      </div>
    </div>
  );
};

const IconPreview = ({ name }) => {
  const Ico = CARD_ICONS[name] || CARD_ICONS.stethoscope;
  return (
    <div className="shrink-0 w-10 h-10 rounded-full border-2 border-gold/70 flex items-center justify-center" data-testid="card-service-icon-preview">
      <Ico size={20} className="text-gold" />
    </div>
  );
};

const LanguagePicker = ({ d, up }) => {
  const sel = d.languages || [];
  const toggle = (c) => up({ languages: sel.includes(c) ? sel.filter((x) => x !== c) : [...sel, c].slice(0, 8) });
  return (
    <div className="space-y-2" data-testid="card-languages-picker">
      <Field label="Τίτλος γλωσσών"><input value={d.langLabel ?? 'Γλώσσες επικοινωνίας'} onChange={(e) => up({ langLabel: e.target.value })} className={inputCls} data-testid="card-lang-label-input" /></Field>
      <span className="block text-xs uppercase tracking-wider text-gold/80">Γλώσσες επικοινωνίας ιατρού (έως 8)</span>
      <div className="flex flex-wrap gap-2">
        {Object.entries(CARD_LANGUAGES).map(([c, name]) => (
          <button key={c} type="button" onClick={() => toggle(c)} className={`flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs transition-colors ${sel.includes(c) ? 'border-gold bg-gold/20 text-gold' : 'border-gold/20 text-slate-300 hover:border-gold/50'}`} data-testid={`card-lang-${c}`}>
            <img src={`/card/flags/${c}.png`} alt="" className="w-5 h-3.5 object-cover rounded-sm" /> {name}
          </button>
        ))}
      </div>
    </div>
  );
};

const ServicesEditor = ({ services, onChange }) => {
  const set = (i, patch) => onChange(services.map((s, j) => (j === i ? { ...s, ...patch } : s)));
  return (
    <div className="space-y-2" data-testid="card-services-editor">
      {services.map((s, i) => (
        <div key={i} className="flex gap-2 items-start">
          <IconPreview name={s.icon} />
          <select value={s.icon} onChange={(e) => set(i, { icon: e.target.value })} className="w-44 shrink-0 bg-navy-200/60 border border-gold/20 rounded-lg px-2 py-2 text-sm text-white" data-testid={`card-service-icon-${i}`}>
            {Object.keys(CARD_ICONS).map((k) => <option key={k} value={k}>{CARD_ICON_LABELS[k] || k}</option>)}
          </select>
          <textarea rows={2} value={s.text} onChange={(e) => set(i, { text: e.target.value })} className={`${inputCls} flex-1 min-w-0`} data-testid={`card-service-text-${i}`} />
          <button type="button" onClick={() => onChange(services.filter((_, j) => j !== i))} className="p-2 text-red-400 hover:text-red-300" data-testid={`card-service-remove-${i}`}><Trash2 size={16} /></button>
        </div>
      ))}
      {services.length < 8 && (
        <button type="button" onClick={() => onChange([...services, { icon: 'stethoscope', text: '' }])} className="text-gold text-sm flex items-center gap-1" data-testid="card-service-add">
          <Plus size={14} /> Προσθήκη υπηρεσίας
        </button>
      )}
    </div>
  );
};

const SavedList = ({ cards, currentId, onOpen, onDelete, onNew }) => (
  <div className="space-y-2" data-testid="card-saved-list">
    <button onClick={onNew} className="w-full flex items-center justify-center gap-2 border border-gold/40 text-gold rounded-lg py-2 text-sm hover:bg-gold/10" data-testid="card-new-btn">
      <FilePlus2 size={16} /> Νέα κάρτα
    </button>
    {cards.map((c) => (
      <div key={c.id} className={`flex items-center gap-2 rounded-lg px-3 py-2 text-sm cursor-pointer ${c.id === currentId ? 'bg-gold/15 text-gold' : 'bg-navy-200/40 text-slate-200 hover:bg-navy-200/70'}`} onClick={() => onOpen(c)} data-testid={`card-saved-${c.id}`}>
        <span className="flex-1 truncate">{c.name}</span>
        <button onClick={(e) => { e.stopPropagation(); onDelete(c); }} className="text-red-400 hover:text-red-300" data-testid={`card-delete-${c.id}`}><Trash2 size={14} /></button>
      </div>
    ))}
    {cards.length === 0 && <p className="text-xs text-slate-500">Δεν υπάρχουν αποθηκευμένες κάρτες.</p>}
  </div>
);

export default function DoctorCardStudio() {
  const [d, setD] = useState(SAMPLE);
  const [cards, setCards] = useState([]);
  const [currentId, setCurrentId] = useState(null);
  const [busy, setBusy] = useState(false);
  const storyRef = useRef(null);
  const postRef = useRef(null);
  const up = (patch) => setD((p) => ({ ...p, ...patch }));

  const load = async () => {
    const { data } = await axios.get(`${API}/admin/doctor-cards`);
    setCards(data);
  };
  useEffect(() => {
    if (!document.getElementById('card-fonts-css')) {
      const l = document.createElement('link');
      l.id = 'card-fonts-css'; l.rel = 'stylesheet'; l.href = '/card/card-fonts.css';
      document.head.appendChild(l);
    }
    load().catch(() => toast.error('Αποτυχία φόρτωσης καρτών'));
  }, []);

  const uploadPhoto = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append('file', file);
    try {
      const { data } = await axios.post(`${API}/admin/line-collaborators/upload-photo`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      up({ photo: `${process.env.REACT_APP_BACKEND_URL}${data.photo_url}`, photoX: 50, photoY: 30, photoZoom: 100 });
      toast.success('Η φωτογραφία ανέβηκε');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Αποτυχία ανεβάσματος');
    }
  };

  const save = async () => {
    setBusy(true);
    try {
      const body = { name: d.name || 'Χωρίς όνομα', data: d };
      const { data } = currentId ? await axios.put(`${API}/admin/doctor-cards/${currentId}`, body) : await axios.post(`${API}/admin/doctor-cards`, body);
      setCurrentId(data.id);
      await load();
      toast.success('Η κάρτα αποθηκεύτηκε');
    } catch (e) {
      toast.error('Αποτυχία αποθήκευσης');
    } finally {
      setBusy(false);
    }
  };

  const remove = async (c) => {
    if (!window.confirm(`Διαγραφή της κάρτας «${c.name}»;`)) return;
    await axios.delete(`${API}/admin/doctor-cards/${c.id}`);
    if (c.id === currentId) { setCurrentId(null); setD(EMPTY); }
    load();
  };

  const download = async (format) => {
    const node = format === 'story' ? storyRef.current : postRef.current;
    const { w, h } = CARD_FORMATS[format];
    setBusy(true);
    try {
      await document.fonts.ready;
      const blob = await toBlob(node, { width: w, height: h, pixelRatio: 1, cacheBust: true, style: { transform: 'none' } });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${(d.name || 'karta').replace(/[^\p{L}\p{N}]+/gu, '_')}_${w}x${h}.png`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    } catch (e) {
      toast.error('Αποτυχία δημιουργίας εικόνας');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="grid grid-cols-1 xl:grid-cols-[220px_minmax(0,1fr)_auto] gap-6" data-testid="doctor-card-studio">
      <SavedList cards={cards} currentId={currentId} onOpen={(c) => { setCurrentId(c.id); setD({ ...EMPTY, ...c.data }); }} onDelete={remove} onNew={() => { setCurrentId(null); setD(EMPTY); }} />

      <div className="space-y-4 bg-navy-100/60 border border-gold/15 rounded-xl p-5">
        <h3 className="font-playfair text-xl text-gold">Κάρτα Γιατρού</h3>
        <Field label="Φωτογραφία">
          <label className="flex items-center gap-2 cursor-pointer text-sm text-gold border border-dashed border-gold/40 rounded-lg px-3 py-2 hover:bg-gold/5" data-testid="card-photo-upload">
            <Upload size={16} /> Ανέβασμα φωτογραφίας
            <input type="file" accept="image/*" className="hidden" onChange={(e) => uploadPhoto(e.target.files?.[0])} data-testid="card-photo-input" />
          </label>
        </Field>
        <div className="grid grid-cols-3 gap-3">
          <Field label="Οριζόντια"><input type="range" min="0" max="100" value={d.photoX} onChange={(e) => up({ photoX: +e.target.value })} className="w-full accent-[#d4af37]" data-testid="card-photo-x" /></Field>
          <Field label="Κάθετα"><input type="range" min="0" max="100" value={d.photoY} onChange={(e) => up({ photoY: +e.target.value })} className="w-full accent-[#d4af37]" data-testid="card-photo-y" /></Field>
          <Field label="Ζουμ"><input type="range" min="100" max="200" value={d.photoZoom} onChange={(e) => up({ photoZoom: +e.target.value })} className="w-full accent-[#d4af37]" data-testid="card-photo-zoom" /></Field>
        </div>
        <Field label="Στυλ διακόσμησης πάνω & κάτω">
          <div className="grid grid-cols-2 gap-2" data-testid="card-decor-options">
            {[['line', 'Απλή λεπτή χρυσή γραμμή'], ['wave', 'Κυματιστό χρυσό μοτίβο']].map(([k, label]) => (
              <button key={k} type="button" onClick={() => up({ decor: k })} className={`rounded-lg border px-3 py-2 text-sm transition-colors ${(d.decor || 'line') === k ? 'border-gold bg-gold/15 text-gold' : 'border-gold/20 text-slate-300 hover:border-gold/50'}`} data-testid={`card-decor-${k}`}>{label}</button>
            ))}
          </div>
        </Field>
        {d.decor === 'wave' && (
        <Field label={`Πάχος κυματιστού μοτίβου: ${d.waveT ?? 100}%`}>
          <div className="flex items-center gap-3">
            <input type="range" min="20" max="150" value={d.waveT ?? 100} onChange={(e) => up({ waveT: +e.target.value })} className="w-full accent-[#d4af37]" data-testid="card-wave-thickness-slider" />
            <input type="number" min="20" max="150" value={d.waveT ?? 100} onChange={(e) => up({ waveT: Math.max(20, Math.min(150, +e.target.value || 20)) })} className="w-20 bg-navy-200/60 border border-gold/20 rounded-lg px-2 py-1 text-sm text-white" data-testid="card-wave-thickness-input" />
          </div>
        </Field>
        )}
        {(d.decor || 'line') === 'line' && (
        <Field label={`Πάχος χρυσής γραμμής: ${d.lineT ?? 3}px`}>
          <div className="flex items-center gap-3">
            <input type="range" min="1" max="40" value={d.lineT ?? 3} onChange={(e) => up({ lineT: +e.target.value })} className="w-full accent-[#d4af37]" data-testid="card-line-thickness-slider" />
            <input type="number" min="1" max="40" value={d.lineT ?? 3} onChange={(e) => up({ lineT: Math.max(1, Math.min(40, +e.target.value || 1)) })} className="w-20 bg-navy-200/60 border border-gold/20 rounded-lg px-2 py-1 text-sm text-white" data-testid="card-line-thickness-input" />
          </div>
        </Field>
        )}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Field label="Ονοματεπώνυμο"><input value={d.name} onChange={(e) => up({ name: e.target.value })} className={inputCls} data-testid="card-name-input" /></Field>
          <Field label="Ειδικότητα"><input value={d.title} onChange={(e) => up({ title: e.target.value })} className={inputCls} data-testid="card-title-input" /></Field>
        </div>
        <StyleControl d={d} up={up} k="name" label="Ονοματεπώνυμο" />
        <StyleControl d={d} up={up} k="title" label="Ειδικότητα" />
        <StyleControl d={d} up={up} k="intro" label="Κείμενο εισαγωγής (πάνω δεξιά)" />
        <Field label="Κείμενο παρουσίασης (κενή γραμμή = νέα παράγραφος)"><textarea rows={5} value={d.bio} onChange={(e) => up({ bio: e.target.value })} className={inputCls} data-testid="card-bio-input" /></Field>
        <StyleControl d={d} up={up} k="bio" label="Κείμενο παρουσίασης" />
        <Field label="Οι υπηρεσίες μας (έως 8)"><ServicesEditor services={d.services} onChange={(services) => up({ services })} /></Field>
        <StyleControl d={d} up={up} k="services" label="Υπηρεσίες" />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div className="space-y-2">
            <Field label="Τίτλος κουτιού 1"><input value={d.phonesLabel ?? 'Τηλέφωνα επικοινωνίας'} onChange={(e) => up({ phonesLabel: e.target.value })} className={inputCls} data-testid="card-phones-label-input" /></Field>
            <Field label="Κείμενο κουτιού 1"><textarea rows={2} value={d.phones} onChange={(e) => up({ phones: e.target.value })} className={inputCls} data-testid="card-phones-input" /></Field>
          </div>
          <div className="space-y-2">
            <Field label="Τίτλος κουτιού 2"><input value={d.locationLabel ?? 'Τοποθεσία ιατρείου'} onChange={(e) => up({ locationLabel: e.target.value })} className={inputCls} data-testid="card-location-label-input" /></Field>
            <Field label="Κείμενο κουτιού 2"><textarea rows={2} value={d.location} onChange={(e) => up({ location: e.target.value })} className={inputCls} data-testid="card-location-input" /></Field>
          </div>
          <div className="space-y-2">
            <Field label="Τίτλος κουτιού 3 (προαιρετικό)"><input value={d.followLabel ?? 'Follow us on'} onChange={(e) => up({ followLabel: e.target.value })} className={inputCls} placeholder="π.χ. Follow us on" data-testid="card-follow-label-input" /></Field>
            <Field label="Κείμενο κουτιού 3 (προαιρετικό)"><textarea rows={2} value={d.follow || ''} onChange={(e) => up({ follow: e.target.value })} className={inputCls} data-testid="card-follow-input" /></Field>
          </div>
        </div>
        <StyleControl d={d} up={up} k="boxes" label="Κάτω κουτάκια" />
        <LanguagePicker d={d} up={up} />
        <StyleControl d={d} up={up} k="langs" label="Γλώσσες" />
        <div className="flex flex-wrap gap-3 pt-2">
          <button onClick={save} disabled={busy} className="flex items-center gap-2 bg-gold text-navy font-semibold rounded-lg px-4 py-2 hover:brightness-110 disabled:opacity-50" data-testid="card-save-btn"><Save size={16} /> {currentId ? 'Αποθήκευση αλλαγών' : 'Αποθήκευση κάρτας'}</button>
          <button onClick={() => download('story')} disabled={busy} className="flex items-center gap-2 border border-gold text-gold rounded-lg px-4 py-2 hover:bg-gold/10 disabled:opacity-50" data-testid="card-download-story-btn"><Download size={16} /> PNG 1080×1920</button>
          <button onClick={() => download('post')} disabled={busy} className="flex items-center gap-2 border border-gold text-gold rounded-lg px-4 py-2 hover:bg-gold/10 disabled:opacity-50" data-testid="card-download-post-btn"><Download size={16} /> PNG 1080×1350</button>
        </div>
      </div>

      <div className="space-y-4">
        <p className="text-xs text-slate-400 uppercase tracking-wider">Story 1080×1920</p>
        <ScaledPreview d={d} format="story" canvasRef={storyRef} />
        <p className="text-xs text-slate-400 uppercase tracking-wider">Post 1080×1350</p>
        <ScaledPreview d={d} format="post" canvasRef={postRef} />
      </div>
    </div>
  );
}
